import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { toast } from "sonner";
import { ArrowCounterClockwise, TrashSimple } from "@phosphor-icons/react";
import { useConfirm } from "@/components/ConfirmProvider";

const KIND_LABEL = {
  kb: "Knowledge Base",
  stories: "Storybank",
  swipes: "Swipe File",
  posts: "Planner",
  comments: "Comments",
  povs: "POV & Ideas",
};

export default function TrashPage() {
  const confirm = useConfirm();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/trash");
      setItems(r.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const restore = async (it) => {
    setBusy(`${it.kind}-${it.id}`);
    try {
      await api.post(`/trash/${it.kind}/${it.id}/restore`);
      toast.success(`${KIND_LABEL[it.kind]} item restored.`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(""); }
  };

  const restoreAllOfKind = async (kind) => {
    const label = KIND_LABEL[kind] || kind;
    const count = items.filter(x => x.kind === kind).length;
    const ok = await confirm({
      title: `Restore all ${label}?`,
      description: `${count} item${count === 1 ? "" : "s"} will move back into ${label}. You can trash them again anytime.`,
      confirmText: `Restore ${count}`,
    });
    if (!ok) return;
    setBusy(`all-${kind}`);
    try {
      const r = await api.post(`/trash/${kind}/restore-all`);
      toast.success(`${r.data.restored} ${label} item${r.data.restored === 1 ? "" : "s"} restored.`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(""); }
  };

  const purge = async (it) => {
    const ok = await confirm({
      title: "Delete forever?",
      description: `"${it.title}" will be permanently removed. This cannot be undone.`,
      confirmText: "Delete forever",
    });
    if (!ok) return;
    setBusy(`${it.kind}-${it.id}`);
    try {
      await api.delete(`/trash/${it.kind}/${it.id}`);
      toast.success("Deleted permanently.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(""); }
  };

  const filtered = filter === "all" ? items : items.filter(i => i.kind === filter);
  const counts = items.reduce((acc, it) => { acc[it.kind] = (acc[it.kind] || 0) + 1; return acc; }, {});

  return (
    <div>
      <PageHeader
        eyebrow="Trash"
        title="Nothing is ever really gone."
        subtitle="Anything you deleted from Knowledge Base, Storybank, Swipe File, or Planner lives here. Restore it in one click, or delete it forever when you're sure."
      />

      <div className="mb-6 flex flex-wrap items-center gap-2" data-testid="trash-filters">
        <button data-testid="trash-filter-all" onClick={() => setFilter("all")} className="chip cursor-pointer" style={filter === "all" ? { background: "var(--accent)", color: "#fff", borderColor: "var(--accent)" } : {}}>
          All · {items.length}
        </button>
        {Object.entries(KIND_LABEL).map(([k, label]) => (
          <button
            key={k}
            data-testid={`trash-filter-${k}`}
            onClick={() => setFilter(k)}
            className="chip cursor-pointer"
            style={filter === k ? { background: "var(--accent)", color: "#fff", borderColor: "var(--accent)" } : {}}
          >
            {label} · {counts[k] || 0}
          </button>
        ))}
        {filter !== "all" && (counts[filter] || 0) > 0 && (
          <button
            data-testid={`trash-restore-all-${filter}`}
            onClick={() => restoreAllOfKind(filter)}
            disabled={busy === `all-${filter}`}
            className="chip cursor-pointer ml-auto flex items-center gap-1"
            style={{ background: "var(--accent-tint)", color: "var(--accent-2)", border: "1px solid var(--accent)" }}
          >
            <ArrowCounterClockwise size={12}/> {busy === `all-${filter}` ? "Restoring…" : `Restore all ${counts[filter]} ${KIND_LABEL[filter]}`}
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-sm" style={{ color: "var(--ink-3)" }}>Loading trash…</div>
      ) : filtered.length === 0 ? (
        <div className="card p-16 text-center" data-testid="trash-empty">
          <div className="eyebrow mb-2">Empty</div>
          <p className="font-serif text-3xl mb-3">Your trash is clean.</p>
          <p className="text-sm max-w-md mx-auto" style={{ color: "var(--ink-2)" }}>
            When you delete something anywhere in BrandLoop, it lands here first — safe to restore for as long as you want.
          </p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="trash-list">
          {filtered.map((it) => (
            <div key={`${it.kind}-${it.id}`} data-testid={`trash-item-${it.id}`} className="card p-4 flex items-center gap-4 fade-up">
              <span className="chip" style={{ minWidth: 130, justifyContent: "center" }}>{KIND_LABEL[it.kind] || it.kind_label}</span>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm truncate">{it.title || "Untitled"}</div>
                <div className="text-xs font-mono" style={{ color: "var(--ink-3)" }}>
                  Deleted {it.deleted_at ? new Date(it.deleted_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                </div>
              </div>
              <button
                data-testid={`trash-restore-${it.id}`}
                onClick={() => restore(it)}
                disabled={busy === `${it.kind}-${it.id}`}
                className="btn btn-accent text-xs py-1.5 px-3"
              >
                <ArrowCounterClockwise size={13} /> Restore
              </button>
              <button
                data-testid={`trash-purge-${it.id}`}
                onClick={() => purge(it)}
                disabled={busy === `${it.kind}-${it.id}`}
                className="btn btn-ghost text-xs py-1.5 px-3"
                title="Delete forever"
              >
                <TrashSimple size={13} /> Delete forever
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
